import React from 'react'
import DynamicTemplateRenderer from './DynamicTemplateRenderer'
import templateService from '../services/TemplateService'
import { PDFDownloader } from '../utils/PDFDownloader'
import Toast from '../utils/Toast'
import { Download, X } from 'lucide-react'
import { withRfpTemplateFields } from '../utils/withRfpTemplateFields'
import { QuotationDataMapper } from '../utils/QuotationDataMapper'
import { COMPANY_BRANCHES, DEFAULT_BRANCH } from '../config/appConfig'

export default function PIPreview({ piData, companyBranches, user, onClose }) {
  const [templateHtml, setTemplateHtml] = React.useState('')
  const [loading, setLoading] = React.useState(false)

  // Inject party-credit adjustment section into PI templates at render-time (no DB template edits).
  const injectPartyCreditIntoPiTemplate = React.useCallback((html) => {
    if (!html || typeof html !== 'string') return html
  
    const hasPartyCredit = html.toLowerCase().includes('party credit')
    const hasNetPayable = html.toLowerCase().includes('net payable')
  
    if (hasPartyCredit && hasNetPayable) return html
  
    // ✅ SAME COLOR BOTH SIDES (IMPORTANT FIX)
    const rows = `
      ${!hasPartyCredit ? `
        <tr>
          <td style="
            text-align:right;
            padding:8px 10px;
            font-size:13px;
            background:#f1f5f9;
            font-weight:500;
          ">
            Party Credit Adjustment
          </td>
          <td style="
            text-align:right;
            padding:8px 10px;
            font-size:13px;
            background:#f1f5f9;
            font-weight:500;
          ">
            ₹{{partyCreditAppliedFormatted}}
          </td>
        </tr>
      ` : ''}
  
      ${!hasNetPayable ? `
        <tr>
          <td style="
            text-align:right;
            padding:10px;
            font-size:14px;
            font-weight:700;
            background:#e2e8f0;
          ">
            Net Payable
          </td>
          <td style="
            text-align:right;
            padding:10px;
            font-size:14px;
            font-weight:700;
            background:#e2e8f0;
          ">
            ₹{{piNetPayableAfterCreditFormatted}}
          </td>
        </tr>
      ` : ''}
    `
  
    return html.replace(
      /(TOTAL AMOUNT[\s\S]*?<\/tr>)/,
      `$1${rows}`
    )
  }, [])

  React.useEffect(() => {
    const templateKey = piData?.template || piData?.templateKey
    
    if (!templateKey) {
      Toast.error('PI template key not found. Please ensure PI was created with a template.')
      return
    }
    
    setLoading(true)
    templateService.getTemplateByTypeAndKey('pi', templateKey)
      .then(tpl => {
        setTemplateHtml(tpl?.html_content || '')
      })
      .catch(() => {
        Toast.error('Failed to load PI template')
      })
      .finally(() => setLoading(false))
  }, [piData?.template, piData?.templateKey])

  const handleDownload = async () => {
    try {
      const piNumber = piData.invoiceNumber || piData.piNumber || 'PI'
      const customerName = piData.billTo?.business || 'Customer'
      await PDFDownloader.downloadPI(piNumber, customerName)
      Toast.success('PI PDF downloaded successfully')
    } catch (error) {
      Toast.error('Failed to download PDF')
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-700">Loading PI template...</span>
          </div>
        </div>
      </div>
    )
  }
  
  if (!templateHtml) {
    return (
      <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md">
          <div className="text-center">
            <div className="text-red-600 text-5xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold mb-2">Template Not Found</h3>
            <p className="text-gray-600 mb-4">
              This PI has no template configured or the template could not be loaded.
              Please ensure the PI was created with a valid PI template.
            </p>
            <button 
              onClick={onClose} 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Resolve branch: companyBranches may be empty (fetch not done) or keyed by UUID vs 'ANODE'
  const branch =
    (piData.selectedBranch && companyBranches?.[piData.selectedBranch]) ||
    (companyBranches && Object.values(companyBranches)[0]) ||
    (DEFAULT_BRANCH && COMPANY_BRANCHES?.[DEFAULT_BRANCH]) ||
    (COMPANY_BRANCHES && Object.values(COMPANY_BRANCHES)[0]) ||
    {}
  const branchName = branch?.name || branch?.companyName || branch?.legalName || 'ANODE ELECTRIC PVT. LTD.'
  const bankDetails =
    QuotationDataMapper.normalizeBankDetails(piData.bankDetails, branchName) ||
    {
      branch: branchName,
      branchName,
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      accountHolderName: branchName,
      account_holder_name: branchName
    }

  // Party credit adjustment fields: ensure templates can show it for saved PIs too.
  const creditAdjustedRaw =
    Number(
      piData?.credit_adjusted ??
      piData?.creditAdjusted ??
      piData?.creditAdjustment ??
      piData?.credit_adjustment ??
      0
    ) || 0
  const creditAdjustedRounded = Math.round(creditAdjustedRaw * 100) / 100
  const totalRaw = Number(piData?.total_amount ?? piData?.totalAmount ?? piData?.total ?? 0) || 0
  const netPayableRaw = Math.max(0, totalRaw - creditAdjustedRounded)
  const formatMoney = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const partyCreditContext = {
    // Show these lines only if party-credit actually adjusted (after rounding)
    partyCreditAdjustEnabled: creditAdjustedRounded > 0,
    partyCreditApplied: creditAdjustedRounded,
    partyCreditAppliedFormatted: formatMoney(creditAdjustedRounded),
    piTotalBeforeCredit: totalRaw,
    piTotalBeforeCreditFormatted: formatMoney(totalRaw),
    piNetPayableAfterCredit: netPayableRaw,
    piNetPayableAfterCreditFormatted: formatMoney(netPayableRaw),
  }

  return (
    <div className="fixed inset-0 z-[120] overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-2 sm:p-4 flex-1 overflow-y-auto">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0 mb-4">
            <h3 className="text-base sm:text-lg font-semibold">PI - {piData.billTo?.business || 'Customer'}</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 self-end sm:self-auto"><X className="h-5 w-5" /></button>
          </div>
          <div id="pi-preview-content">
            <DynamicTemplateRenderer
              html={injectPartyCreditIntoPiTemplate(templateHtml)}
              data={withRfpTemplateFields({
                ...piData,
                ...partyCreditContext,
                branch,
                billTo: piData.billTo,
                bankDetails,
                user,
                templateKey: piData.template,
                templateType: 'pi'
              })}
              containerId="pi-content"
            />
          </div>
        </div>
        <div className="bg-gray-50 px-3 sm:px-4 py-2 sm:py-3 border-t border-gray-200 flex flex-col sm:flex-row sm:justify-end gap-2 sm:space-x-3">
          <button onClick={onClose} className="px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50">Close</button>
          <button onClick={handleDownload} className="px-3 py-1.5 text-xs sm:text-sm font-medium text-white bg-green-600 border border-transparent rounded shadow-sm hover:bg-green-700 flex items-center justify-center gap-2"><Download className="w-4 h-4" />Download PDF</button>
        </div>
      </div>
    </div>
  )
}
